// Per-class "what to roll" advice, derived from the empirical cross-tab win
// rates in rosterStats.crossByClass (win rate of each Support/Reaction/Move
// skill WHEN CARRIED BY a given class). Inverts that map to answer "for this
// class, which skills in each slot have historically won most?" — so the
// generator's dice helper and the strategy page give real, class-specific
// picks instead of generic folklore.
//
// Correlational, not causal (good players pick good skills), and it only
// reflects skills that actually appear on the class often enough to clear the
// sample floor — but it's grounded in years of real outcomes.

// Only ever suggest FORCEABLE picks — the stream tokens a player can actually force
// on a roll (Wizard Mantle yes, Chantage/Salty Rage no; Short Charge yes, the gated
// Math Skill / Doppelganger no). Matched case-insensitively by display name.
// The set is INJECTED (opts.forceable, built by makeForceable from genTokens.json)
// so this module stays pure and raw-Node-testable — a direct JSON import needs an
// import attribute on modern Node. Components import classAdviceLive.js, which
// binds the bundled JSON (same split as simCalibration/-Live). No set ⇒ no filter.
export function makeForceable(genTokens) {
  return new Set(Object.values(genTokens.byToken || {}).map((t) => (t.name || '').toLowerCase()));
}
// Hidden skills (below) are forceable tokens too, since genTokens lists them so the What
// Chat Forces page recognizes them, so they're excluded here by name, not by absence.
const forceable = (fset, skill) => !HIDDEN_SKILLS.has(skill) && (!fset || fset.has((skill || '').toLowerCase()));

const SLOTS = [
  ['ActionSkill', 'Secondary'],   // secondary skillset — forceable
  ['SupportSkill', 'Support'],
  ['ReactionSkill', 'Reaction'],
  ['MoveSkill', 'Move'],
  ['Accessory', 'Accessory'],     // forceable accessory
];

// "Hidden" skills — JP-gated / special, only long-time players can roll them, so
// recommending them to a newcomer is useless. Excluded from all advice. (Of
// these, only Blade Grasp / Teleport 2 actually appear in the support/reaction/
// move cross-tab; the rest are secondary skillsets / specials, listed for
// completeness so the filter stays correct if the data ever surfaces them.)
export const HIDDEN_SKILLS = new Set([
  'Math Skill', 'Doppelganger', 'Equip Perfume', 'Blade Grasp', 'Teleport 2', 'Raid Boss',
]);

// Find a class entry in a cross-tab array. With a gender, only that gender's row counts.
// It used to fall back to the combined row, which gave a gender figures for skillsets it
// can never roll: the Classes page offered Sing as a ban for a female Time Mage, using
// the male Time Mage's 44.5%. Female units never roll Sing and males never roll Dance,
// so neither has a row for the other gender. Without a gender, Male+Female combine
// (weighted avg winRate, summed played).
function findCross(arr, cls, gender) {
  if (!arr) return null;
  if (gender === 'Male' || gender === 'Female') return arr.find((x) => x.value === cls + ' ' + gender) || null;
  const plain = arr.find((x) => x.value === cls);
  if (plain) return plain;
  const m = arr.find((x) => x.value === cls + ' Male');
  const f = arr.find((x) => x.value === cls + ' Female');
  if (!m && !f) return null;
  if (!m) return f;
  if (!f) return m;
  const played = m.played + f.played;
  return { value: cls, winRate: Math.round(((m.winRate * m.played + f.winRate * f.played) / played) * 10) / 10, played };
}

// Top `n` skills for `cls` in dimension `dim`, ranked by win rate, with a
// sample floor so a 3-game fluke doesn't top the list.
function topForSlot(rosterStats, cls, dim, n, minPlayed, gender, fset) {
  const cb = (rosterStats.crossByClass && rosterStats.crossByClass[dim]) || {};
  const out = [];
  for (const skill in cb) {
    if (!forceable(fset, skill)) continue;  // only suggest forceable tokens
    const hit = findCross(cb[skill], cls, gender);
    if (hit && hit.played >= minPlayed) out.push({ skill, winRate: hit.winRate, played: hit.played });
  }
  return out.sort((a, b) => b.winRate - a.winRate).slice(0, n);
}

// Full advice for one class: { Support:[…], Reaction:[…], Move:[…] }, each a
// ranked list of { skill, winRate, played }. `opts.n` picks per slot (default 3),
// `opts.minPlayed` sample floor (default 150).
export function classAdvice(rosterStats, cls, opts = {}) {
  const n = opts.n || 3, minPlayed = opts.minPlayed || 150;
  const out = {};
  for (const [dim, label] of SLOTS) out[label] = topForSlot(rosterStats, cls, dim, n, minPlayed, opts.gender, opts.forceable);
  return out;
}

// Skills to AVOID for a class: the lowest-win-rate picks across all three slots,
// EXCLUDING the obvious "Equip <weapon>" off-class noise (always bad, not
// interesting) and hidden skills. Surfaces genuinely tempting-but-weak rolls
// (e.g. Martial Arts on an Archer). Returns up to `n` { skill, winRate }.
export function avoidPicks(rosterStats, cls, opts = {}) {
  const n = opts.n || 2, minPlayed = opts.minPlayed || 300, ceiling = opts.ceiling || 49;
  const all = [];
  for (const [dim] of SLOTS) {
    const cb = (rosterStats.crossByClass && rosterStats.crossByClass[dim]) || {};
    for (const skill in cb) {
      if (!forceable(opts.forceable, skill) || /^Equip /.test(skill)) continue;
      const hit = findCross(cb[skill], cls, opts.gender);
      if (hit && hit.played >= minPlayed && hit.winRate < ceiling) all.push({ skill, winRate: hit.winRate });
    }
  }
  return all.sort((a, b) => a.winRate - b.winRate).slice(0, n);
}

// Full per-category breakdown for the class strategy modal: for each forceable slot
// (Secondary / Support / Reaction / Move / Accessory) the top `n` BEST and bottom `n`
// WORST by win rate, with a sample floor. Worst excludes off-class "Equip <weapon>"
// (always-bad noise) like avoidPicks. Returns { Secondary:{best,worst}, … }.
export function classFullAdvice(rosterStats, cls, opts = {}) {
  const n = opts.n || 5, minPlayed = opts.minPlayed || 120;
  const out = {};
  for (const [dim, label] of SLOTS) {
    const cb = (rosterStats.crossByClass && rosterStats.crossByClass[dim]) || {};
    const rows = [];
    for (const skill in cb) {
      if (!forceable(opts.forceable, skill)) continue;
      const hit = findCross(cb[skill], cls, opts.gender);
      if (hit && hit.played >= minPlayed) rows.push({ skill, winRate: hit.winRate, played: hit.played });
    }
    const best = [...rows].sort((a, b) => b.winRate - a.winRate).slice(0, n);
    const worst = rows.filter((r) => !/^Equip /.test(r.skill)).sort((a, b) => a.winRate - b.winRate).slice(0, n);
    out[label] = { best, worst, total: rows.length };
  }
  return out;
}

// Which gender is stronger for a class, if there's a meaningful gap. Reads the
// ClassGender win-rate dim. Returns { gender:'Male'|'Female', delta } or null
// (monsters / thin data / negligible difference). `classKey` is the class name.
export function genderEdge(rosterStats, cls, opts = {}) {
  const minDelta = opts.minDelta || 1.0, minPlayed = opts.minPlayed || 500;
  const cg = (rosterStats.windows && rosterStats.windows.all && rosterStats.windows.all.ClassGender) || [];
  const m = cg.find((x) => x.value === cls + ' Male');
  const f = cg.find((x) => x.value === cls + ' Female');
  if (!m || !f || m.played < minPlayed || f.played < minPlayed) return null;
  const delta = +(Math.abs(m.winRate - f.winRate)).toFixed(1);
  if (delta < minDelta) return null;
  return { gender: m.winRate >= f.winRate ? 'Male' : 'Female', delta };
}
